import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  Send, 
  Shield, 
  Target, 
  TrendingUp, 
  Activity,
  Zap,
  Filter,
  BarChart3,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const TelegramLandingPage = () => {
  const TELEGRAM_LINK = "https://t.me/BorsamexTR";

  // SEO Structured Data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Borsamex Telegram Kanalı - Canlı Sistem Telemetrisi",
    "description": "Yapay zeka destekli işlem motorunun canlı telemetrisini takip edin. Gerçek zamanlı TP/SL sonuçları, filtrelenen işlemler ve sistem kararları.",
    "url": "https://borsamex.com/telegram",
    "publisher": {
      "@type": "Organization",
      "name": "Borsamex",
      "url": "https://borsamex.com"
    },
    "mainEntity": {
      "@type": "SocialMediaPosting",
      "sharedContent": {
        "@type": "WebPage",
        "url": "https://t.me/BorsamexTR"
      }
    }
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const systemExamples = [
    { icon: Zap, text: "Execution Event Detected", color: "text-emerald-400" },
    { icon: Shield, text: "Risk Toleransı Düşürüldü", color: "text-yellow-400" },
    { icon: Filter, text: "12 Kötü Setup Filtrelendi", color: "text-red-400" },
    { icon: BarChart3, text: "Telemetry Report Published", color: "text-green-400" }
  ];

  const features = [
    { icon: Target, text: "Gerçekleşen TP / Stop sonuçları" },
    { icon: Filter, text: "Reddedilen (filtrelenen) işlemler" },
    { icon: Activity, text: "Günlük sistem telemetrisi" }
  ];

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Borsamex Telegram Kanalı | Canlı AI Trading Telemetrisi</title>
        <meta name="description" content="Yapay zeka destekli işlem motorunun canlı telemetrisini Telegram'dan takip edin. Gerçek zamanlı TP/SL sonuçları, filtrelenen işlemler ve sistem kararları. Ücretsiz katılın!" />
        <meta name="keywords" content="borsamex telegram, kripto sinyal telegram, ai trading telegram, canlı trading sinyalleri, telegram kripto kanalı, borsamex kanal, ücretsiz kripto sinyal" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://borsamex.com/telegram" />
        <meta property="og:title" content="Borsamex Telegram Kanalı | Canlı AI Trading Telemetrisi" />
        <meta property="og:description" content="Yapay zeka destekli işlem motorunun canlı telemetrisini takip edin. Gerçek zamanlı TP/SL sonuçları ve sistem kararları." />
        <meta property="og:image" content="https://borsamex.com/og-image.png" />
        <meta property="og:locale" content="tr_TR" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://borsamex.com/telegram" />
        <meta name="twitter:title" content="Borsamex Telegram Kanalı | Canlı AI Trading Telemetrisi" />
        <meta name="twitter:description" content="Yapay zeka destekli işlem motorunun canlı telemetrisini takip edin. Ücretsiz katılın!" />
        <meta name="twitter:image" content="https://borsamex.com/og-image.png" />
        
        {/* Canonical */}
        <link rel="canonical" href="https://borsamex.com/telegram" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-white overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-green-500/5 rounded-full blur-3xl" />
        </div>

        {/* Hero Section */}
        <section className="relative pt-20 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Live Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/30 rounded-full px-4 py-2 mb-8"
            >
              <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </span>
            <span className="text-red-400 text-sm font-medium">CANLI YAYIN</span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
          >
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-400">
              Yapay Zekâ Destekli
            </span>
            <span className="block text-white">
              İşlem Motoru
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.2 }}
            className="text-gray-400 text-lg md:text-xl mb-4"
          >
            Piyasa Hareketlerini İzler. Fırsatları Filtreler. Sinyalleri Üretir.
          </motion.p>

          {/* Bottom Line */}
          <motion.p
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.3 }}
            className="text-xl md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-400 mb-10"
          >
            Tahmin Değil — Sistem Kararları & Canlı Telemetri
          </motion.p>

          {/* Primary CTA */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.4 }}
            className="mb-6"
          >
            <a
              href={TELEGRAM_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-telegram-cta"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-bold text-lg px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25"
            >
              <Send className="w-5 h-5" />
              Telegram Canlı Akışına Katıl
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>

          {/* Micro Trust Text */}
          <motion.p
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.5 }}
            className="text-gray-500 text-sm"
          >
            Ücretsiz erişim • Anlık güncellemeler • Reklam yok
          </motion.p>
        </div>
      </section>

      {/* Trust Builder Section */}
      <section className="relative py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 md:p-12"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-2xl md:text-3xl font-bold text-center mb-10"
            >
              Sistem Ne Yayınlıyor?
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="flex items-center gap-4 bg-gray-800/50 rounded-xl p-4"
                >
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <span className="text-gray-300">{feature.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Authority / Differentiation Block */}
      <section className="relative py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-6">
              Klasik Sinyal Kanalı{' '}
              <span className="text-red-400">Değiliz</span>
            </h2>
            
            <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              Borsamex, piyasayı yorumlayan bir ekip değil,
              <br />
              <span className="text-white font-medium">
                piyasa koşullarına reaksiyon veren algoritmik bir sistemdir.
              </span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Curiosity Trigger Section */}
      <section className="relative py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-2xl md:text-3xl font-bold text-center mb-10"
          >
            Sistem Davranış Örnekleri
          </motion.h2>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-4"
          >
            {systemExamples.map((example, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-gray-900/70 border border-gray-800 rounded-xl p-5 flex items-center gap-4 hover:border-gray-700 transition-colors"
              >
                <div className={`w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center ${example.color}`}>
                  <example.icon className="w-5 h-5" />
                </div>
                <span className="text-gray-300 font-mono text-sm md:text-base">
                  {example.text}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Main Conversion Block */}
      <section className="relative py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-emerald-900/30 to-green-900/30 border border-emerald-500/20 rounded-3xl p-8 md:p-12 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Canlı Akışı{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                Kaçırma
              </span>
            </h2>

            <p className="text-gray-400 text-lg mb-8">
              Sistem çıktıları yalnızca Telegram kanalında yayınlanır.
            </p>

            <a
              href={TELEGRAM_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="main-telegram-cta"
              className="inline-flex items-center gap-3 bg-white hover:bg-gray-100 text-gray-900 font-bold text-lg px-10 py-5 rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl"
            >
              <Send className="w-6 h-6" />
              Telegram Kanalına Katıl
            </a>

            <p className="text-gray-500 text-sm mt-6">
              Üyelik ücretsizdir • Çıkış serbesttir
            </p>
          </motion.div>
        </div>
      </section>

      {/* Social Proof Mini Section */}
      <section className="relative py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-500">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>7/24 Otomatik</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Şeffaf Sonuçlar</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Kurumsal Kalite</span>
            </div>
          </div>
        </div>
      </section>

      {/* Minimal Disclaimer Footer - No Navigation */}
      <footer className="relative py-6 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-600 text-xs leading-relaxed">
            © 2026 Borsamex · Paylaşımlar yatırım tavsiyesi değildir.
            <br />
            Geçmiş performans gelecekteki sonuçların garantisi değildir.
          </p>
        </div>
      </footer>
      </div>
    </>
  );
};

export default TelegramLandingPage;
